import { LabelType, type Participant, type User } from '@piccolohealth/pbs-common';
import { ChakraV3, type Row, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedLabelChooser } from '../../components/forms/HookedLabelChooser';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { useCreateParticipantUsersMutation } from '../../graphql/hooks/useCreateParticipantUsersMutation';
import { useParticipantsFilterQuery } from '../../graphql/hooks/useParticipantsQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { useParticipantsFilter } from '../../hooks/useParticipantsFilter';
import { ParticipantsFilterControls } from '../participant/ParticipantsFilterControls';
import { UserParticipantsAddTable } from './UserParticipantsAddTable';

type FormValues = {
  participantIds: string[];
  labelIds: string[];
};

interface Props {
  user: User;
}

export const UserParticipantAddModal = createModal<Props>((props) => {
  const { user, modal } = props;
  const { visible, hide, remove } = modal;

  const { organization, successToast, errorToast } = useAppContext();

  const filter = useParticipantsFilter();
  const { isLoading, error, participants, rowSelection, refetch, pagination } =
    useParticipantsFilterQuery(filter);

  const { selectedRows } = rowSelection;

  const methods = useForm<FormValues>({
    defaultValues: {
      participantIds: [],
      labelIds: [],
    },
  });

  const { setValue, watch, reset } = methods;

  const selectedParticipants = watch('participantIds');

  const mutation = useCreateParticipantUsersMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          requests: values.participantIds.map((participantId) => ({
            participantId,
            userId: user.id,
            labelIds: values.labelIds,
          })),
        })
        .then(() => {
          rowSelection.onRowsSelect([]);
          reset();
          successToast('Participants added to user successfully');
        })
        .catch((err) => {
          errorToast(`Error adding participants to user: ${err.message}`);
        });

      return;
    },
    [errorToast, mutation, organization.id, reset, rowSelection, successToast, user.id],
  );

  const enableRowSelection = React.useCallback(
    (row: Row<Participant>) =>
      P.isEmpty(row.original.assignedUsers) ||
      row.original.assignedUsers.every((u) => u.user.id !== user.id),
    [user.id],
  );

  // https://github.com/biomejs/biome/issues/630
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to run this effect when the participants change
  React.useEffect(() => {
    setValue(
      'participantIds',
      selectedRows.map((participant) => participant.id),
      {
        shouldDirty: true,
      },
    );
  }, [participants, selectedRows, setValue]);

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content minH='fit-content'>
              <ChakraV3.DialogTitleSubtitle
                title='Add participants to user'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ChakraV3.Stack h='full' gap={6}>
                  <ChakraV3.Stack>
                    <ParticipantsFilterControls filter={filter} />
                  </ChakraV3.Stack>
                  <ScrollArea overflowY='auto' minH='420px'>
                    <UserParticipantsAddTable
                      user={user}
                      participants={participants}
                      isLoading={isLoading}
                      error={error}
                      refetch={refetch}
                      pagination={pagination}
                      rowSelection={{ ...rowSelection, enableRowSelection }}
                    />
                  </ScrollArea>
                  <ChakraV3.Stack>
                    <ChakraV3.HStack
                      w='full'
                      bg='gray.50'
                      layerStyle='outline.subtle'
                      px={8}
                      py={4}
                      rounded='md'
                      align='end'
                    >
                      <FormStack w='4xl' orientation='horizontal'>
                        <HookedFormField
                          name='participantids'
                          label='Participants'
                          helperText='The participants to assign this user'
                        >
                          <ChakraV3.Text fontSize='sm'>
                            {P.isEmpty(selectedParticipants)
                              ? 'No participants selected'
                              : `${selectedParticipants.length} ${inflection.inflect(
                                  'participant',
                                  selectedParticipants.length,
                                )} selected`}
                          </ChakraV3.Text>
                        </HookedFormField>
                        <HookedFormField
                          name='labelIds'
                          label='Labels'
                          helperText='The labels to assign this user'
                          flexGrow={1}
                        >
                          <HookedLabelChooser
                            name='labelIds'
                            type={LabelType.ParticipantUser}
                            bg='white'
                          />
                        </HookedFormField>
                      </FormStack>
                      <ChakraV3.Spacer />
                      <ChakraV3.HStack>
                        <ChakraV3.Button size='xs' onClick={hide} variant='subtle'>
                          Close
                        </ChakraV3.Button>
                        <HookedSubmitButton size='xs'>Add participants</HookedSubmitButton>
                      </ChakraV3.HStack>
                    </ChakraV3.HStack>
                  </ChakraV3.Stack>
                </ChakraV3.Stack>
              </ChakraV3.Dialog.Body>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
